import { useEffect, useState } from 'react'
import axios from 'axios';
import { Helmet } from "react-helmet";
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Zap, Shield, Award, Eye, Code, Cloud, Database, Cpu, Smartphone, Globe } from 'lucide-react';
import Loader from '../components/Loader';

const AllProducts = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [hoveredCard, setHoveredCard] = useState(null);

    useEffect(() => {
        fetchData();
        window.scrollTo(0, 0);
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('https://emr.metadei.in/wp-json/wp/v2/posts?per_page=100');
            const data = Array.isArray(response.data) ? response.data : [];
            const filteredPosts = data.filter(post => post.categories?.includes(4));
            setProducts(filteredPosts);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching products:', error);
            setLoading(false);
        }
    };

    // Helper function to extract image URL from object or string
    const getImageUrl = (imageField) => {
        if (!imageField) return null;
        if (typeof imageField === 'string' && (imageField.startsWith('http') || imageField.startsWith('/'))) {
            return imageField.replace(/^http:\/\/emr\.metadei\.in/, 'https://emr.metadei.in');
        }
        if (typeof imageField === 'object' && imageField.url) {
            return imageField.url.replace(/^http:\/\/emr\.metadei\.in/, 'https://emr.metadei.in');
        }
        return null;
    }

    // Helper function to check if SCF data exists
    const hasSCFData = (product) => {
        return product?.acf && Object.keys(product.acf).length > 0 &&
            !(product.acf.length === 0);
    }

    // Get product image (priority: SCF product_icon → seo_image_posts → fallback)
    const getProductImage = (product) => {
        if (hasSCFData(product) && product.acf.product_icon) {
            const iconUrl = getImageUrl(product.acf.product_icon);
            if (iconUrl) return iconUrl;
        }
        const seoImage = getImageUrl(product.acf?.seo_image_posts);
        if (seoImage) return seoImage;
        return null;
    }

    // Get product description (priority: SCF product_short_desc → SEO excerpt → excerpt)
    const getProductDescription = (product) => {
        if (hasSCFData(product) && product.acf.product_short_desc) {
            return product.acf.product_short_desc;
        }
        if (product.acf?.SEO_excerpt_posts) {
            return product.acf.SEO_excerpt_posts;
        }
        if (product.excerpt?.rendered) {
            return product.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 120);
        }
        return 'Discover our elite software solution designed to dominate your market operations.';
    }

    // Get product subtitle (from SCF)
    const getProductSubtitle = (product) => {
        if (hasSCFData(product) && product.acf.product_subtitle) {
            return product.acf.product_subtitle;
        }
        return null;
    }

    // Get key features count (to display badge)
    const getFeaturesCount = (product) => {
        if (hasSCFData(product) && product.acf.key_features && product.acf.key_features.length > 0) {
            return product.acf.key_features.length;
        }
        return 0;
    }

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50, filter: 'blur(10px)' },
        visible: {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            transition: { duration: 0.8, type: 'spring', bounce: 0.4 }
        }
    };

    const icons = [Sparkles, Zap, Shield, Award, Eye, Code, Cloud, Database, Cpu, Smartphone, Globe];

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <Helmet>
                <title>Products - Connected Bytes Software Service</title>
                <meta property="og:title" content='Products - Connected Bytes Software Service' />
                <meta property="og:description" content='Discover our innovative products delivering real results through custom web, mobile, and enterprise applications' />
                <meta property="og:image" content='https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN' />
                <meta property="og:url" content='https://connectedbytes.in/products' />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content='summary_large_image' />
                <meta name="twitter:title" content='Products - Connected Bytes Software Service' />
                <meta name="twitter:description" content='Discover our innovative products delivering real results' />
                <meta name="twitter:image" content='https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN' />
            </Helmet>

            <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
                {/* Hero Section */}
                {/* <div className="relative pt-32 pb-24 overflow-hidden border-b border-border">
                    <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 1px)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 blur-[150px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/4" />
                    <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/10 blur-[150px] rounded-full pointer-events-none translate-y-1/2 -translate-x-1/4" />

                    <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            transition={{ duration: 0.8, type: 'spring' }}
                        >
                            <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-4">Our Arsenal</span>
                            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black font-heading mb-6 tracking-tight">
                                Elite Solutions
                                <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mt-2">
                                    For Market Giants
                                </span>
                            </h1>
                            <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-8 shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />
                            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-body leading-relaxed">
                                Deploy our proprietary, cutting-edge architectures to brutally optimize your operations, hyper-scale productivity, and dominate your sector.
                            </p>
                        </motion.div>
                    </div>
                </div> */}

                {/* Products Grid */}
                <div className="max-w-7xl mx-auto px-4 py-24 relative z-10">
                    {products.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-muted-foreground font-body">No products available at this time.</p>
                        </div>
                    ) : (
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                        >
                            {products.map((product, index) => {
                                const IconComponent = icons[index % icons.length];
                                const productImage = getProductImage(product);
                                const productDesc = getProductDescription(product);
                                const productSubtitle = getProductSubtitle(product);
                                const featuresCount = getFeaturesCount(product);

                                return (
                                    <motion.div
                                        key={product.id}
                                        variants={cardVariants}
                                        onMouseEnter={() => setHoveredCard(index)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                        className="group relative cursor-pointer block h-full"
                                        onClick={() => navigate(`/products/${product.slug}`)}
                                    >
                                        <div className="relative bg-card/60 backdrop-blur-md rounded-3xl overflow-hidden shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-border transition-all duration-500 h-full flex flex-col hover-glow">


                                            {/* Image Container */}
                                            <div className="relative h-64 overflow-hidden bg-gradient-to-br from-muted to-background flex-shrink-0 border-b border-border">
                                                <motion.div
                                                    animate={hoveredCard === index ? { scale: 1.05 } : { scale: 1 }}
                                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                                    className="h-full w-full"
                                                >
                                                    {productImage ? (
                                                        <img
                                                            src={productImage}
                                                            alt={product.title?.rendered || 'Product'}
                                                            className="w-full h-full object-contain p-8 drop-shadow-2xl"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                                                            <IconComponent className="w-16 h-16 text-primary drop-shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />
                                                        </div>
                                                    )}
                                                </motion.div>



                                                {/* Icon Badge */}
                                                <div className="absolute top-4 right-4 w-12 h-12 rounded-2xl bg-card/90 backdrop-blur-md flex items-center justify-center shadow-lg border border-border/50 transform translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 delay-100 z-20">
                                                    <IconComponent className="w-6 h-6 text-primary" />
                                                </div>
                                            </div>

                                            {/* Card Content */}
                                            <div className="p-8 flex flex-col flex-grow relative z-20">
                                                <div className="mb-6">
                                                    <h3 className="text-2xl font-bold font-heading mb-3 line-clamp-2 transition-colors duration-300 min-h-[64px] group-hover:text-primary">
                                                        {product.title?.rendered || 'Product Title'}
                                                    </h3>
                                                    {/* {productSubtitle && (
                                                        <p className="text-xs font-bold text-primary uppercase tracking-widest font-secondary mb-2">
                                                            {productSubtitle}
                                                        </p>
                                                    )} */}
                                                    <div className="w-12 h-1 bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-500 group-hover:w-24 opacity-50 group-hover:opacity-100" />
                                                </div>

                                                {/* <p className="text-muted-foreground text-sm leading-relaxed font-body mb-6 line-clamp-3 flex-grow">
                                                    {productDesc}
                                                </p> */}

                                                {/* Tags - dynamically from SCF or default */}
                                                {/* <div className="flex flex-wrap gap-2 mb-6">
                                                    {hasSCFData(product) && product.acf.key_features && product.acf.key_features.length > 0 ? (
                                                        product.acf.key_features.slice(0, 2).map((feature, idx) => (
                                                            <span key={idx} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 bg-muted text-muted-foreground rounded-full border border-border/50">
                                                                {feature.feature_title?.length > 20 ? feature.feature_title.substring(0, 18) + '...' : feature.feature_title}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <>
                                                            <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 bg-muted text-muted-foreground rounded-full border border-border/50">AI-Engine</span>
                                                            <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 bg-muted text-muted-foreground rounded-full border border-border/50">Enterprise</span>
                                                        </>
                                                    )}
                                                </div> */}

                                                {/* Button */}
                                                <motion.button
                                                    whileHover={{ x: 5 }}
                                                    className="mt-auto inline-flex items-center gap-2 text-primary font-bold font-secondary text-sm group/btn uppercase tracking-widest pt-4 border-t border-border/30"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/products/${product.slug}`);
                                                    }}
                                                >
                                                    <span>Explore Architecture</span>
                                                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                                                </motion.button>
                                            </div>

                                        </div>

                                        {/* Glow Effect */}
                                        <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-accent rounded-[1.8rem] opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 pointer-events-none -z-10" />
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}
                </div>

                {/* CTA Section */}
                <div className="py-24 relative overflow-hidden border-t border-border mt-8">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 pointer-events-none" />
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[400px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
                    <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
                        <h2 className="text-4xl md:text-5xl font-black font-heading mb-6">
                            Ready to Transform Your Infrastructure?
                        </h2>
                        <p className="text-xl text-muted-foreground mb-10 font-body">
                            Let's discuss how our elite proprietary products can radically digitize your processes.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/contact')}
                            className="px-10 py-4 bg-primary text-primary-foreground rounded-full font-bold shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-all duration-300 inline-flex items-center gap-3 font-secondary text-lg"
                        >
                            Initiate Engagement <ArrowRight className="w-5 h-5" />
                        </motion.button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AllProducts;